import type { CanvasDocumentState } from '../canvas/documentModel';
import type { DragDraft } from '../canvas/gesture';
import { executeTemplateBuilderOperation } from './execute';
import type { TemplateOperationExtras } from './operationExtrasContext';
import type { TemplateOperationStore } from './types';

/**
 * Applies a finished canvas drag to the template registry as one logical commit.
 * Single-element drags emit one move_element; multi-element drags emit one move per id
 * from the same pointer-up (in-session undo remains on CommandRegistry).
 */
export async function applyCommittedDragToRegistry(
  store: TemplateOperationStore,
  draft: DragDraft,
  committedDocument: CanvasDocumentState,
  operationExtras: TemplateOperationExtras = {},
): Promise<void> {
  const moves: Array<{ elementId: string; x: number; y: number }> = [];

  for (const elementId of draft.elementIds) {
    const start = draft.startPositions[elementId];
    const layout = committedDocument.elements.find((entry) => entry.id === elementId);
    if (!start || !layout) {
      continue;
    }
    if (layout.x === start.x && layout.y === start.y) {
      continue;
    }
    moves.push({ elementId, x: layout.x, y: layout.y });
  }

  if (moves.length === 0) {
    return;
  }

  // Start every move before awaiting so each store write lands in the same turn.
  const pending = moves.map((move) =>
    executeTemplateBuilderOperation(
      { source: 'ui', store, ...operationExtras },
      {
        operation: 'move_element',
        args: move,
      },
    ),
  );
  await Promise.all(pending);
}
